import React, { useState } from 'react'
import { InputNumber, Modal, Select, Steps, Tag, Typography, Upload, message } from 'antd'
import { useMsal } from '@azure/msal-react'
import { DeleteOutlined, DownloadOutlined, InboxOutlined, InfoCircleOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons'
import Button from '../../components/common/button/button'
import Input from '../../components/common/input/input'
import Card from '../../components/common/card/card'
import Table from '../../components/common/table/table'
import AppModal from '../../components/common/modal/modal'
import AddProductModal from './AddProductModal'
import {
  MAX_NOTE_LENGTH,
  MAX_NOTES_LENGTH,
  MAX_PRODUCTS_PER_MOVEMENT,
  MOVEMENT_STATUS_OPTIONS,
  MOVEMENT_STATUS_COLORS,
  REASON_OPTIONS,
  formatReason,
  parseProductsCsv
} from './inventoryMovementConstants'
import {
  createInventoryMovement,
  getApiErrorMessage,
  lookupInventoryProducts
} from '../../api/inventoryMovementApiService/inventoryMovementApiUtils'
import './NewMovementView.scss'

const { Title, Text } = Typography

const STEP_ITEMS = [
  { title: 'Add Products' },
  { title: 'Review & Create' }
]

const TEMPLATE_HEADERS = ['Keycode', 'Quantity', 'FromStatus', 'ToStatus', 'Reason', 'Note']


const toCsvRow = (values) => values.map(value => {
  const text = String(value)
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}).join(',')

const requiredTitle = (label) => (
  <span>
    <span className="new-movement__required-marker" aria-hidden="true">*</span>
    {label}
  </span>
)

const buildRow = (product, { fromMovementStatus = 'Good', toMovementStatus = 'Hold' } = {}) => ({
  rowId: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  keycode: product.keycode,
  productName: product.productName,
  quantity: null,
  fromMovementStatus,
  toMovementStatus,
  reasonCode: '',
  note: ''
})

const NewMovementView = ({ onCancel, onCreate }) => {
  const { instance } = useMsal()
  const [currentStep, setCurrentStep] = useState(0)
  const [notes, setNotes] = useState('')
  const [rows, setRows] = useState([])
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false)
  const [selectedCsvFile, setSelectedCsvFile] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isCsvProcessing, setIsCsvProcessing] = useState(false)

  const updateRow = (rowId, changes) => {
    setRows(current => current.map(row => (row.rowId === rowId ? { ...row, ...changes } : row)))
  }

  const removeRow = (rowId) => {
    setRows(current => current.filter(row => row.rowId !== rowId))
  }

  const addProducts = (products, statuses) => {
    const existingKeycodes = rows.map(row => row.keycode)
    const duplicates = products.filter(product => existingKeycodes.includes(product.keycode))
    const newRows = products
      .filter(product => !existingKeycodes.includes(product.keycode))
      .map(product => buildRow(product, statuses))
    if (newRows.length > 0) {
      setRows(current => [...current, ...newRows])
    }
    if (duplicates.length > 0) {
      Modal.warning({
        title: 'Duplicate keycode skipped',
        content: `Already added, so skipped: ${duplicates.map(product => product.keycode).join(', ')}`
      })
    }
    setIsAddModalOpen(false)
  }

  const addCsvRows = async (csvText) => {
    const { rows: parsedRows, errors } = parseProductsCsv(csvText)
    const seenKeycodes = new Set(rows.map(row => row.keycode))
    const duplicates = []
    const uniqueParsedRows = []
    parsedRows.forEach(row => {
      if (seenKeycodes.has(row.keycode)) {
        duplicates.push(row)
      } else {
        seenKeycodes.add(row.keycode)
        uniqueParsedRows.push(row)
      }
    })

    if (rows.length + uniqueParsedRows.length > MAX_PRODUCTS_PER_MOVEMENT) {
      message.error(`A movement can contain at most ${MAX_PRODUCTS_PER_MOVEMENT} products`)
      return
    }

    const newRows = []
    if (uniqueParsedRows.length > 0) {
      const { products } = await lookupInventoryProducts(instance, uniqueParsedRows.map(row => row.keycode))
      const productsByKeycode = new Map(products.map(product => [product.keycode, product]))
      uniqueParsedRows.forEach(row => {
        const product = productsByKeycode.get(row.keycode)
        if (!product) {
          errors.push(`Row ${row.rowNumber}: unknown keycode "${row.keycode}"`)
          return
        }
        newRows.push({
          ...buildRow(product, row),
          quantity: row.quantity,
          reasonCode: row.reasonCode,
          note: row.note
        })
      })
    }

    if (newRows.length > 0) {
      setRows(current => [...current, ...newRows])
      message.success(`${newRows.length} product${newRows.length > 1 ? 's' : ''} added from CSV`)
    }
    if (duplicates.length > 0) {
      Modal.warning({
        title: 'Duplicate keycode skipped',
        content: `Already added, so skipped: ${duplicates.map(row => row.keycode).join(', ')}`
      })
    }
    if (errors.length > 0) {
      message.warning(`${errors.length} row${errors.length > 1 ? 's' : ''} skipped: ${errors.slice(0, 3).join('; ')}`)
    }
  }

  const closeCsvModal = () => {
    if (isCsvProcessing) return
    setIsCsvModalOpen(false)
    setSelectedCsvFile(null)
  }

  const downloadCsvTemplate = () => {
    const csv = `${toCsvRow(TEMPLATE_HEADERS)}\r\n`
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'inventory_movement_template.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const selectCsvFile = (file) => {
    if (!/\.csv$/i.test(file.name)) {
      message.error('Please select a CSV file')
      return Upload.LIST_IGNORE
    }
    setSelectedCsvFile(file)
    return false
  }

  const handleCsvUpload = () => {
    if (!selectedCsvFile) {
      message.warning('Select or drop a CSV file to upload')
      return
    }

    const processFile = async () => {
      setIsCsvProcessing(true)
      try {
        const csvText = await selectedCsvFile.text()
        await addCsvRows(csvText)
        setIsCsvModalOpen(false)
        setSelectedCsvFile(null)
      } catch (error) {
        message.error(getApiErrorMessage(error, 'Failed to process the inventory movement file'))
      } finally {
        setIsCsvProcessing(false)
      }
    }
    processFile()
  }

  const goBack = () => setCurrentStep(step => Math.max(0, step - 1))

  const goNext = () => {
    if (rows.length === 0) {
      message.error('Add at least one product before continuing')
      return
    }
    if (rows.length > MAX_PRODUCTS_PER_MOVEMENT) {
      message.error(`A movement can contain at most ${MAX_PRODUCTS_PER_MOVEMENT} products`)
      return
    }
    if (rows.some(row => row.quantity !== null && row.quantity !== undefined && (!Number.isInteger(row.quantity) || row.quantity <= 0))) {
      message.error('Quantity must be a positive number')
      return
    }
    const sameStatusRows = rows.filter(row => row.fromMovementStatus === row.toMovementStatus)
    if (sameStatusRows.length > 0) {
      message.error(`From and To status must differ: ${sameStatusRows.map(row => row.keycode).join(', ')}`)
      return
    }
    const damagedRowsWithoutReason = rows.filter(row => row.toMovementStatus === 'Damaged' && !row.reasonCode)
    if (damagedRowsWithoutReason.length > 0) {
      message.error(`Reason is required for damaged products: ${damagedRowsWithoutReason.map(row => row.keycode).join(', ')}`)
      return
    }
    setCurrentStep(step => Math.min(STEP_ITEMS.length - 1, step + 1))
  }

  const handleCreate = async () => {
    setIsCreating(true)
    try {
      const result = await createInventoryMovement(instance, {
        notes: notes.trim(),
        products: rows.map(row => ({
          keycode: row.keycode,
          quantity: row.quantity,
          fromMovementStatus: row.fromMovementStatus,
          toMovementStatus: row.toMovementStatus,
          reasonCode: row.reasonCode,
          note: row.note.trim()
        }))
      })
      onCreate(result)
    } catch (error) {
      message.error(getApiErrorMessage(error, 'Failed to create inventory movement'))
    } finally {
      setIsCreating(false)
    }
  }

  const statusColumn = (editable, title, field) => ({
    title,
    dataIndex: field,
    key: field,
    width: 140,
    render: (value, record) => (
      editable
        ? (
          <Select
            value={value}
            status={record.fromMovementStatus === record.toMovementStatus ? 'error' : undefined}
            className="new-movement__condition-select"
            options={MOVEMENT_STATUS_OPTIONS.map(option => ({ value: option, label: option }))}
            onChange={newValue => updateRow(record.rowId, { [field]: newValue })}
          />
          )
        : <Tag color={MOVEMENT_STATUS_COLORS[value]}>{value}</Tag>
    )
  })

  const productColumns = (editable) => [
    { title: 'Keycode', dataIndex: 'keycode', key: 'keycode', width: 120 },
    { title: 'Product Name', dataIndex: 'productName', key: 'productName', width: 200 },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      render: (value, record) => (
        editable
          ? (
            <InputNumber
              value={value}
              min={1}
              precision={0}
              placeholder="Enter quantity"
              className="new-movement__quantity-input"
              onChange={newValue => updateRow(record.rowId, { quantity: newValue })}
            />
            )
          : value
      )
    },
    statusColumn(editable, 'From Status', 'fromMovementStatus'),
    statusColumn(editable, 'To Status', 'toMovementStatus'),
    {
      title: editable && rows.some(row => row.toMovementStatus === 'Damaged') ? requiredTitle('Reason') : 'Reason',
      dataIndex: 'reasonCode',
      key: 'reasonCode',
      width: 220,
      render: (value, record) => (
        editable
          ? (
            <Select
              value={value || undefined}
              placeholder="Select reason"
              allowClear
              className="new-movement__reason-select"
              options={REASON_OPTIONS.map(option => ({ value: option.id, label: `${option.id} - ${option.label}` }))}
              onChange={newValue => updateRow(record.rowId, { reasonCode: newValue || '' })}
            />
            )
          : (formatReason(value) || '—')
      )
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      width: 200,
      render: (value, record) => (
        editable
          ? (
            <Input
              value={value}
              placeholder="Add note"
              maxLength={MAX_NOTE_LENGTH}
              onChange={event => updateRow(record.rowId, { note: event.target.value })}
            />
            )
          : (value || '—')
      )
    },
    ...(editable
      ? [{
          title: 'Actions',
          key: 'actions',
          width: 70,
          render: (_, record) => (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeRow(record.rowId)}
              aria-label="Remove product"
            />
          )
        }]
      : [])
  ]

  return (
    <section className="new-movement">
      <div className="new-movement__header">
        <Title level={2}>New Inventory Movement</Title>
        <Text type="secondary">Add products and set their condition changes.</Text>
      </div>

      <Steps current={currentStep} items={STEP_ITEMS} className="new-movement__steps" />

      {currentStep === 0 && (
        <>
          <Card
            title="Add Products"
            className="new-movement__card"
            extra={(
              <div className="new-movement__card-actions">
                <Button icon={<UploadOutlined />} onClick={() => setIsCsvModalOpen(true)}>Upload CSV</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAddModalOpen(true)}>
                  Add Product
                </Button>
              </div>
            )}
          >
            <Table
              dataSource={rows}
              columns={productColumns(true)}
              rowKey="rowId"
              pagination={false}
              scroll={{ x: 1270 }}
              className="new-movement__table"
              locale={{ emptyText: 'No products added yet' }}
              rowHoverable={false}
            />
          </Card>

          <Card title="Notes" className="new-movement__card">
            <Input
              type="textarea"
              value={notes}
              placeholder="Add any notes about this movement..."
              maxLength={MAX_NOTES_LENGTH}
              showCount
              autoSize={{ minRows: 3, maxRows: 5 }}
              onChange={event => setNotes(event.target.value)}
            />
          </Card>
        </>
      )}

      {currentStep === 1 && (
        <>
          <Card title="Products" className="new-movement__card">
            <Table
              dataSource={rows}
              columns={productColumns(false)}
              rowKey="rowId"
              pagination={false}
              scroll={{ x: 1070 }}
              className="new-movement__table"
              rowHoverable={false}
            />
          </Card>
          <Card title="Notes" className="new-movement__card">
            <div className="new-movement__review-value">{notes || '—'}</div>
          </Card>
        </>
      )}

      <div className="new-movement__footer">
        <Button onClick={currentStep === 0 ? onCancel : goBack} disabled={isCreating}>
          {currentStep === 0 ? 'Cancel' : 'Back'}
        </Button>
        {currentStep === STEP_ITEMS.length - 1
          ? <Button type="primary" loading={isCreating} onClick={handleCreate}>Create Movement</Button>
          : <Button type="primary" onClick={goNext}>Next</Button>}
      </div>

      <AddProductModal
        open={isAddModalOpen}
        onAdd={addProducts}
        onClose={() => setIsAddModalOpen(false)}
      />

      <AppModal
        title="Upload Inventory Movement File"
        open={isCsvModalOpen}
        width={820}
        okText="Upload"
        confirmLoading={isCsvProcessing}
        maskClosable={!isCsvProcessing}
        onOk={handleCsvUpload}
        onCancel={closeCsvModal}
      >
        <div className="new-movement__csv-template-header">
          <div>
            <Text strong>CSV template</Text>
            <Text type="secondary">Use these column names and formats.</Text>
          </div>
          <Button icon={<DownloadOutlined />} onClick={downloadCsvTemplate}>Download Template</Button>
        </div>

        <div className="new-movement__csv-template-note">
          <InfoCircleOutlined aria-hidden="true" />
          <span><strong>Status codes:</strong> G = Good, H = Hold, D = Damaged. <strong>Reason codes:</strong> R1-R5.</span>
        </div>

        <div className="new-movement__csv-template-table" role="table" aria-label="Inventory movement template columns">
          {TEMPLATE_HEADERS.map(header => (
            <div key={header} role="columnheader">{header}</div>
          ))}
          <div role="cell">43495573</div>
          <div role="cell">2</div>
          <div role="cell">G</div>
          <div role="cell">H</div>
          <div role="cell">R1</div>
          <div role="cell">Moved to hold for inspection</div>
        </div>
       

        <Upload.Dragger
          accept=".csv,text/csv"
          maxCount={1}
          beforeUpload={selectCsvFile}
          fileList={selectedCsvFile ? [selectedCsvFile] : []}
          disabled={isCsvProcessing}
          onRemove={() => setSelectedCsvFile(null)}
          className="new-movement__csv-dragger"
        >
          <InboxOutlined className="new-movement__csv-upload-icon" />
          <div>Click or drag a CSV file here</div>
          <Text type="secondary">Only one .csv file can be uploaded.</Text>
        </Upload.Dragger>
      </AppModal>
    </section>
  )
}

export default NewMovementView
